import {
  AssetServiceClient,
  AssetSet,
  AssetSetListSortKey,
  AssetSetType,
  ListAssetSetsByProjectResult,
} from '@droidmap/asset-service-contract'
import { AssetContentType, AssetType } from '@droidmap/asset-service-contract'
import {
  MappingJobServiceClient,
  MappingJobStatus,
} from '@droidmap/mapping-job-service-contract'
import {
  Group,
  GroupListSortKey,
  Organisation,
  OrganisationServiceClient,
  OrganisationUser,
  OrganisationUserListSortKey,
} from '@droidmap/organisation-service-contract'
import {
  Grant,
  PermissionsServiceClient,
} from '@droidmap/permissions-service-contract'
import {
  ListProjectsByOrganisationResult,
  Project,
  ProjectListSortKey,
  ProjectServiceClient,
  ProjectStatusFilter,
  Task,
  TaskListSortKey,
  TaskType,
} from '@droidmap/project-service-contract'
import {
  DroidMapMetadata,
  OffsetPaged,
  PaginationCursor,
  SortDirection,
} from '@droidmap/shared-service-contract'

/**
 * Cursor-based pagination options. Used for DDB-backed list endpoints
 * (e.g. permissions grants by user). PG-backed list endpoints take
 * {@link OffsetListOptions} instead.
 */
export type PaginationOptions = {
  limit?: number
  next?: PaginationCursor
}

/**
 * Cursor-based paginated result. Mirror of {@link PaginationOptions}; same
 * scope (DDB-backed list endpoints).
 */
export type PaginatedResult<T> = {
  items: T[]
  next?: PaginationCursor
}

/**
 * Offset-based list options for PG-backed list endpoints. Threaded into
 * the contract clients via {@link OffsetPaginationControls}. Generic over
 * the per-endpoint sort enum.
 */
export type OffsetListOptions<TSortKey extends string = string> = {
  page?: number
  pageSize?: number
  filter?: string
  sortBy?: TSortKey
  sortDir?: SortDirection
}

/**
 * Helper to normalize paginated API responses.
 * Handles null responses and extracts items/next cursor.
 */
async function normalizePaginatedResponse<T>(
  apiCall: Promise<{ items: T[]; next?: PaginationCursor } | null>,
): Promise<PaginatedResult<T>> {
  const response = await apiCall

  if (!response) {
    return { items: [] }
  }

  return {
    items: response.items,
    next: response.next,
  }
}

/**
 * Fetch a page of projects by organisation. Returns the full server response
 * including {@link ListProjectsByOrganisationResult.statusCounts} so the
 * Active/Empty pills can render the full-dataset totals.
 */
export async function fetchProjectsPaginated(
  orgId: string,
  projectClient: ProjectServiceClient,
  metadata: DroidMapMetadata,
  options?: OffsetListOptions<ProjectListSortKey> & {
    /**
     * Scope the listing to a single folder. Omit (`undefined`) for every
     * project in the org; `null` lists only root ("unfiled") projects; a folder
     * id lists that folder's direct projects.
     */
    folderId?: string | null
    status?: ProjectStatusFilter
  },
): Promise<ListProjectsByOrganisationResult> {
  return projectClient.listProjectsByOrganisation(
    {
      orgId,
      page: options?.page,
      pageSize: options?.pageSize,
      filter: options?.filter,
      folderId: options?.folderId,
      sortBy: options?.sortBy,
      sortDir: options?.sortDir,
      status: options?.status,
    },
    metadata,
  )
}

/**
 * Fetch a page of asset sets by project. Returns the full server response
 * including {@link ListAssetSetsByProjectResult.typeCounts} for the
 * All/Drone/ODM toolbar pills.
 */
export async function fetchAssetSetsPaginated(
  projectId: string,
  assetClient: AssetServiceClient,
  metadata: DroidMapMetadata,
  options?: OffsetListOptions<AssetSetListSortKey> & {
    assetSetType?: AssetSetType
    /**
     * Ask the server to decorate each item with its card summary (bounding
     * box + presigned thumbnail). Opt-in — the table view leaves it off to
     * skip the extra per-set thumbnail presign; the grid view sets it.
     */
    includeSummaries?: boolean
  },
): Promise<ListAssetSetsByProjectResult> {
  return assetClient.listAssetSetsByProject(
    {
      projectId,
      page: options?.page,
      pageSize: options?.pageSize,
      filter: options?.filter,
      sortBy: options?.sortBy,
      sortDir: options?.sortDir,
      assetSetType: options?.assetSetType,
      includeSummaries: options?.includeSummaries,
    },
    metadata,
  )
}

/**
 * Fetch a page of tasks by project.
 */
export async function fetchTasksPaginated(
  projectId: string,
  projectClient: ProjectServiceClient,
  metadata: DroidMapMetadata,
  options?: OffsetListOptions<TaskListSortKey>,
): Promise<OffsetPaged<Task>> {
  return projectClient.listTasksByProject(
    {
      projectId,
      page: options?.page,
      pageSize: options?.pageSize,
      filter: options?.filter,
      sortBy: options?.sortBy,
      sortDir: options?.sortDir,
    },
    metadata,
  )
}

/**
 * Fetch a page of groups by organisation.
 */
export async function fetchGroupsPaginated(
  orgId: string,
  organisationClient: OrganisationServiceClient,
  metadata: DroidMapMetadata,
  options?: OffsetListOptions<GroupListSortKey>,
): Promise<OffsetPaged<Group>> {
  return organisationClient.listGroupsByOrganisation(
    {
      orgId,
      page: options?.page,
      pageSize: options?.pageSize,
      filter: options?.filter,
      sortBy: options?.sortBy,
      sortDir: options?.sortDir,
    },
    metadata,
  )
}

/**
 * Fetch a page of organisation users by organisation.
 */
export async function fetchOrganisationUsersPaginated(
  orgId: string,
  organisationClient: OrganisationServiceClient,
  metadata: DroidMapMetadata,
  options?: OffsetListOptions<OrganisationUserListSortKey> & {
    activeOnly?: boolean
  },
): Promise<OffsetPaged<OrganisationUser>> {
  return organisationClient.listOrganisationUsersByOrganisation(
    {
      orgId,
      page: options?.page,
      pageSize: options?.pageSize,
      filter: options?.filter,
      sortBy: options?.sortBy,
      sortDir: options?.sortDir,
      activeOnly: options?.activeOnly,
    },
    metadata,
  )
}

/**
 * Fetch organisations by user with pagination support
 */
export async function fetchOrganisationsPaginated(
  userId: string,
  organisationClient: OrganisationServiceClient,
  metadata: DroidMapMetadata,
  options?: Pick<OffsetListOptions, 'page' | 'pageSize'>,
): Promise<OffsetPaged<Organisation>> {
  return organisationClient.listOrganisationsByUser(
    {
      userId,
      page: options?.page,
      pageSize: options?.pageSize,
    },
    metadata,
  )
}

/**
 * GrantWithId extends Grant to include composite id for DataTable
 *
 * NOTE: This is a workaround because the Grant backend schema doesn't have an 'id' field.
 * Grants are identified by composite key (principalId + resourceId + role) in DynamoDB.
 * Ideally, the backend should add an 'id' field to the Grant model so the frontend
 * doesn't need to reconstruct the composite key. This would be consistent with other
 * entities like Project, Task, etc.
 */
export type GrantWithId = Grant & {
  id: string
}

/**
 * Fetch grants by resource with pagination support. `metadata` must name
 * the resource's org: DroidMap support's grants count only in the request's
 * org (droidmap#2651), so a read-write session listing them under the
 * default `system` is refused.
 */
export async function fetchGrantsPaginated(
  resourceId: string,
  permissionsClient: PermissionsServiceClient,
  metadata: DroidMapMetadata,
  options?: PaginationOptions,
): Promise<PaginatedResult<Grant>> {
  return normalizePaginatedResponse(
    permissionsClient.listGrantsByResource(
      {
        resourceId,
        pageSize: options?.limit,
        next: options?.next,
      },
      metadata,
    ),
  )
}

/**
 * Walk all pages of the user's organisations. Prefer the paginated
 * {@link fetchOrganisationsPaginated} for views — this is for callers that
 * genuinely need every organisation at once.
 */
export async function fetchOrganisations(
  organisationClient: OrganisationServiceClient,
  userId: string,
  metadata: DroidMapMetadata,
): Promise<Organisation[]> {
  const PAGE_SIZE = 100
  const orgs: Organisation[] = []
  let page = 0
  let result: OffsetPaged<Organisation>
  do {
    result = await organisationClient.listOrganisationsByUser(
      { userId, page, pageSize: PAGE_SIZE },
      metadata,
    )
    orgs.push(...result.items)
    page++
  } while (result.items.length === result.pageSize)

  return orgs.filter((org): org is Organisation => org !== undefined)
}

/**
 * Walk all pages of an organisation's users. Prefer the paginated
 * {@link fetchOrganisationUsersPaginated} for views — this is for pickers that
 * must offer every member (e.g. the create-project Access section, #3077).
 */
export async function fetchOrganisationUsers(
  organisationClient: OrganisationServiceClient,
  orgId: string,
  metadata: DroidMapMetadata,
  options?: { activeOnly?: boolean },
): Promise<OrganisationUser[]> {
  const PAGE_SIZE = 100
  const users: OrganisationUser[] = []
  let page = 0
  let result: OffsetPaged<OrganisationUser>
  do {
    result = await organisationClient.listOrganisationUsersByOrganisation(
      { orgId, page, pageSize: PAGE_SIZE, activeOnly: options?.activeOnly },
      metadata,
    )
    users.push(...result.items)
    page++
  } while (result.items.length === result.pageSize)
  return users
}

/**
 * Walk all pages of groups in an organisation. Prefer the paginated
 * {@link fetchGroupsPaginated} for views — this is for callers that
 * genuinely need every group at once.
 */
export async function fetchGroups(
  organisationClient: OrganisationServiceClient,
  orgId: string,
  metadata: DroidMapMetadata,
): Promise<Group[]> {
  const PAGE_SIZE = 100
  const groups: Group[] = []
  let page = 0
  let result: OffsetPaged<Group>
  do {
    result = await organisationClient.listGroupsByOrganisation(
      { orgId, page, pageSize: PAGE_SIZE },
      metadata,
    )
    groups.push(...result.items)
    page++
  } while (result.items.length === result.pageSize)
  return groups
}

/**
 * Walk all pages of projects in an organisation. Prefer the paginated
 * {@link fetchProjectsPaginated} for views — this is for callers that
 * genuinely need every project at once (e.g. cross-project rollups).
 */
export async function fetchProjects(
  orgId: string,
  projectClient: ProjectServiceClient,
  metadata: DroidMapMetadata,
): Promise<Project[]> {
  const PAGE_SIZE = 100
  const projects: Project[] = []
  let page = 0
  let result: ListProjectsByOrganisationResult
  do {
    result = await projectClient.listProjectsByOrganisation(
      { orgId, page, pageSize: PAGE_SIZE },
      metadata,
    )
    projects.push(...result.items)
    page++
  } while (result.items.length === result.pageSize)
  return projects
}

export async function fetchAssetSets(
  projects: Project[],
  assetClient: AssetServiceClient,
  metadata: DroidMapMetadata,
): Promise<AssetSet[]> {
  const PAGE_SIZE = 100
  const allAssetSets: AssetSet[] = []

  for (const project of projects) {
    let page = 0
    let response: ListAssetSetsByProjectResult
    do {
      response = await assetClient.listAssetSetsByProject(
        {
          projectId: project.id,
          page,
          pageSize: PAGE_SIZE,
        },
        metadata,
      )

      if (!response) {
        break
      }

      allAssetSets.push(...response.items)
      page++
    } while (response.items.length === response.pageSize)
  }
  return allAssetSets
}

export async function fetchTasks(
  projects: Project[],
  projectClient: ProjectServiceClient,
  metadata: DroidMapMetadata,
): Promise<Task[]> {
  const PAGE_SIZE = 100
  const allTasks: Task[] = []

  for (const project of projects) {
    let page = 0
    let response: OffsetPaged<Task>
    try {
      do {
        response = await projectClient.listTasksByProject(
          {
            projectId: project.id,
            page,
            pageSize: PAGE_SIZE,
          },
          metadata,
        )
        allTasks.push(...response.items)
        page++
      } while (response.items.length === response.pageSize)
    } catch (error) {
      console.error(`Error fetching tasks for project ${project.id}:`, error)
    }
  }
  return allTasks
}

/**
 * Get all orthomosaic outputs that can be displayed on a map for a task
 */
export const getOrthomosaicOutputs = async (
  task: Task,
  mappingJobClient: MappingJobServiceClient,
  assetClient: AssetServiceClient,
  metadata: DroidMapMetadata,
): Promise<{
  orthomosaics: Array<{
    assetId: string
    assetName: string
    assetType: string
  }>
  mappingJobStatus?: MappingJobStatus
}> => {
  // Only photogrammetry tasks can have orthomosaic outputs
  if (task.type !== TaskType.Photogrammetry || !task.referenceId) {
    return { orthomosaics: [] }
  }

  try {
    // Get the mapping job details
    const mappingJob = await mappingJobClient.getMappingJob(
      { mappingJobId: task.referenceId },
      metadata,
    )

    const orthomosaics: Array<{
      assetId: string
      assetName: string
      assetType: string
    }> = []

    // Check each output asset set for orthomosaic content
    for (const outputAssetSetId of mappingJob.outputAssetSets) {
      const assetSet = await assetClient.getAssetSet(
        { assetSetId: outputAssetSetId },
        metadata,
      )

      // Check if this asset set contains orthomosaic
      if (
        assetSet.containedAssetTypes?.includes(AssetContentType.Orthomosaic)
      ) {
        // Find all TIFF/GeoTIFF/COG assets in this set
        const assetsResult = await assetClient.listAssetsByAssetSet(
          { assetSetId: outputAssetSetId },
          metadata,
        )

        const orthomosaicAssets = assetsResult.items.filter(
          (asset) =>
            asset.assetType === AssetType.Tiff ||
            asset.assetType === AssetType.GeoTiff ||
            asset.assetType === AssetType.Cog,
        )

        orthomosaics.push(
          ...orthomosaicAssets.map((asset) => ({
            assetId: asset.id,
            assetName: asset.name || asset.id,
            assetType: asset.assetType,
          })),
        )
      }
    }

    return {
      orthomosaics,
      mappingJobStatus: mappingJob.status,
    }
  } catch (error) {
    console.error('Error checking for orthomosaic outputs:', error)
    return { orthomosaics: [] }
  }
}

/**
 * Check if a task has orthomosaic outputs that can be displayed on a map
 * @deprecated Use getOrthomosaicOutputs instead for more detailed information
 */
export const hasOrthomosaicOutputs = async (
  task: Task,
  mappingJobClient: MappingJobServiceClient,
  assetClient: AssetServiceClient,
  metadata: DroidMapMetadata,
): Promise<{
  hasOrtho: boolean
  orthomosaicAssetId?: string
  mappingJobStatus?: MappingJobStatus
}> => {
  const result = await getOrthomosaicOutputs(
    task,
    mappingJobClient,
    assetClient,
    metadata,
  )

  return {
    hasOrtho: result.orthomosaics.length > 0,
    orthomosaicAssetId: result.orthomosaics[0]?.assetId,
    mappingJobStatus: result.mappingJobStatus,
  }
}
